import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  CircularProgress,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Phone,
  CheckCircle,
  Refresh
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { formatPhoneForDisplay } from '@/utils/phone-validation';

interface SMSVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  onVerificationComplete: (code: string) => Promise<void>;
  onResendCode: () => Promise<void>;
  isLoading?: boolean;
  title?: string;
  description?: string;
}

interface VerificationForm {
  code: string;
}

export const SMSVerificationModal: React.FC<SMSVerificationModalProps> = ({
  isOpen,
  onClose,
  phoneNumber,
  onVerificationComplete,
  onResendCode,
  isLoading = false,
  title = 'Verify Phone Number',
  description
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<VerificationForm>({ mode: 'onChange' });

  const code = watch('code');

  // Cooldown timer for resend button
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleClose = useCallback(() => {
    setError(null);
    setSuccess(null);
    setIsSubmitting(false);
    setIsResending(false);
    reset();
    onClose();
  }, [reset, onClose]);

  const onSubmitForm = useCallback(async (data: VerificationForm) => {
    if (data.code.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      await onVerificationComplete(data.code);
      setSuccess('Phone number verified successfully!');
      
      // Close modal after success
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error('SMS verification error:', err);
      
      // Handle specific error codes
      switch (err.code) {
        case 'CodeMismatchException':
          setError('Invalid verification code. Please check and try again.');
          break;
        case 'ExpiredCodeException':
          setError('Verification code has expired. Please request a new one.');
          break;
        case 'LimitExceededException':
          setError('Too many attempts. Please wait before trying again.');
          break;
        default:
          setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [onVerificationComplete, handleClose]);

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    if (code && code.length === 6 && /^\d{6}$/.test(code)) {
      handleSubmit(onSubmitForm)();
    }
  }, [code, handleSubmit, onSubmitForm]);

  const handleResendCode = async () => {
    setIsResending(true);
    setError(null);
    setSuccess(null);

    try {
      await onResendCode();
      setSuccess('Verification code sent successfully!');
      setResendCooldown(60); // 60 second cooldown
      reset(); // Clear the code input
    } catch (err: any) {
      console.error('Resend code error:', err);
      setError(err.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
    setValue('code', value);
  };

  const formattedPhone = formatPhoneForDisplay(phoneNumber);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog 
      open={isOpen} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Phone />
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {isLoading && <LinearProgress sx={{ mb: 2 }} />}
        
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CheckCircle color="primary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            {description || `We've sent a 6-digit verification code to:`}
          </Typography>
          <Typography variant="h6" color="primary" gutterBottom>
            {formattedPhone}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter the code below to verify your phone number
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmitForm)} noValidate>
          <TextField
            {...register('code', {
              required: 'Verification code is required',
              pattern: {
                value: /^\d{6}$/,
                message: 'Please enter a 6-digit code'
              }
            })}
            fullWidth
            label="Verification Code"
            placeholder="000000"
            margin="normal"
            error={!!errors.code}
            helperText={errors.code?.message || 'Enter the 6-digit code sent to your phone'}
            disabled={isSubmitting || isLoading}
            onChange={handleCodeChange}
            inputProps={{
              maxLength: 6,
              style: { 
                textAlign: 'center', 
                fontSize: '1.5rem', 
                letterSpacing: '0.5rem',
                fontFamily: 'monospace'
              }
            }}
            autoFocus
            autoComplete="one-time-code"
          />

          {/* Progress indicator for auto-submit */}
          {code && code.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={(code.length / 6) * 100} 
                sx={{ height: 4, borderRadius: 2 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {code.length}/6 digits entered
              </Typography>
            </Box>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={isSubmitting || isLoading || !code || code.length !== 6}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Verify Code'
            )}
          </Button>
        </Box>

        {/* Resend Code Section */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Didn't receive the code?
          </Typography>
          <Button
            variant="text"
            startIcon={isResending ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleResendCode}
            disabled={isResending || resendCooldown > 0 || isLoading}
            sx={{ textTransform: 'none' }}
          >
            {resendCooldown > 0 
              ? `Resend in ${resendCooldown}s` 
              : isResending 
                ? 'Sending...' 
                : 'Resend Code'
            }
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting || isLoading}
          color="inherit"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SMSVerificationModal;